import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';

const LOADING_INDICATOR_DELAY = 180;

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLElement} priceDisplay - Sticky bar's compact price container
 * @property {HTMLElement} comparePrice - Sticky compare-at price
 * @property {HTMLElement} currentPrice - Sticky current price value
 * @property {HTMLElement} paymentType - Sticky payment type label
 * @property {HTMLElement} installmentsSummary - Sticky installments summary
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    'stickyBar',
    'priceDisplay',
    'comparePrice',
    'currentPrice',
    'paymentType',
    'installmentsSummary',
    'addToCartButton',
    'quantityDisplay',
    'quantityNumber',
  ];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {number | undefined} */
  #loadingIndicatorTimeout;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /**
   * Cached footer intersection state. The footer IntersectionObserver only
   * fires on transitions, but on short product pages the footer is already
   * intersecting at page load — so when the user scrolls past the ATC, the
   * ATC observer tries to show the sticky bar with no corresponding footer
   * transition to gate it. Caching the last known intersection state lets
   * #showStickyBar consult it synchronously at decision time.
   * @type {boolean}
   */
  #footerIsVisible = false;

  /** @type {boolean} */
  #isSubmitting = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObservers that drive the sticky bar's visibility.
   *
   * The sticky bar is a stand-in for the main add-to-cart button, so it should
   * appear the instant that button leaves the viewport and disappear the
   * instant it comes back. Observing the broader .buy-buttons-block (which may
   * also wrap accelerated-checkout buttons rendered *after* the ATC) would let
   * the main ATC scroll past while express-checkout buttons kept the block
   * intersecting — delaying the sticky's appearance. Watching the tightest
   * wrapper around the ATC (.add-to-cart-block) keeps the sticky semantically
   * aligned with the action it's substituting.
   *
   * A second observer on the footer keeps the sticky from colliding with
   * footer-level calls-to-action (newsletter, policies) near page bottom.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const mainAddToCartBlock = productForm.querySelector('.add-to-cart-block');
    if (!mainAddToCartBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for main ATC visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar once the main ATC has been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Short-page guard: if the footer is already in the hide zone when
          // the ATC goes out of view, the footer observer won't emit a new
          // transition to hide the bar. Defer the show and mark the state so
          // the bar can pop in later if the footer scrolls back out.
          if (this.#footerIsVisible) {
            this.#hiddenByBottom = true;
          } else {
            this.#showStickyBar();
          }
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        // Cache the state first so the ATC observer can consult it — the
        // footer observer's callback only runs on transitions, so the
        // pre-show guard in the ATC observer needs the latest value here.
        this.#footerIsVisible = entry.isIntersecting;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = mainAddToCartBlock.getBoundingClientRect();
          // Only show if the main ATC is above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(mainAddToCartBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    if (this.#isSubmitting) return;

    this.#isSubmitting = true;
    this.#loadingIndicatorTimeout = window.setTimeout(() => {
      this.#setLoading(true);
    }, LOADING_INDICATOR_DELAY);

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    this.#syncButtonPresentation();
    this.#syncPricePresentation(event.detail.data.html);

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = async (event) => {
    if (!this.#isSubmitting) return;

    const isCartUpdate = event.type === ThemeEvents.cartUpdate;
    const source = event.detail?.data?.source;
    const productId = event.detail?.data?.productId;
    const didError = event.detail?.data?.didError === true;

    if (isCartUpdate) {
      if (source !== 'product-form-component' || productId !== this.dataset.productId) return;
      if (didError) {
        this.#finishSubmit();
        return;
      }

      await this.#playSuccessFeedback();
      this.#finishSubmit();
      return;
    }

    this.#finishSubmit();
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#syncButtonPresentation();
    this.#syncPricePresentation();
    this.#updateButtonText();
  }

  /**
   * Mirrors the main add to cart button presentation in the sticky CTA.
   */
  #syncButtonPresentation() {
    if (!this.#targetAddToCartButton) return;

    const stickyLabel = this.refs.addToCartButton?.querySelector('.add-to-cart-text__content > span:first-child');
    const targetLabel = this.#targetAddToCartButton.querySelector('.add-to-cart-text__content');
    const targetText = this.#targetAddToCartButton.querySelector('.add-to-cart-text');
    const targetBlock = this.#targetAddToCartButton.closest('.add-to-cart-block');

    if (stickyLabel instanceof HTMLElement && targetLabel instanceof HTMLElement) {
      stickyLabel.textContent = targetLabel.textContent?.trim() ?? '';
    }

    const configuredWeight = targetBlock ? getComputedStyle(targetBlock).getPropertyValue('--add-to-cart-font-weight').trim() : '';
    const targetWeight =
      configuredWeight
      || (targetText instanceof HTMLElement ? getComputedStyle(targetText).fontWeight : '')
      || getComputedStyle(this.#targetAddToCartButton).fontWeight;

    if (targetWeight) {
      this.refs.addToCartButton.style.setProperty('--sticky-add-to-cart-font-weight', targetWeight);
    }
  }

  /**
   * Mirrors the main price presentation in the sticky CTA.
   * @param {ParentNode} [sourceRoot]
   */
  #syncPricePresentation(sourceRoot) {
    const sectionRoot = this.closest('.shopify-section');
    const root = sourceRoot ?? sectionRoot;
    if (!root) return;

    const sourcePriceContainer = root.querySelector('product-price [ref="priceContainer"]');
    const sourcePaymentInfo = root.querySelector('product-payment-info [ref="installmentsSummary"]');

    const comparePriceText =
      sourcePriceContainer?.querySelector('.compare-at-price')?.textContent?.trim() ?? '';
    const currentPriceText =
      sourcePriceContainer?.querySelector('.price__current')?.textContent?.trim()
      ?? sourcePriceContainer?.querySelector('.price')?.textContent?.trim()
      ?? '';
    const paymentTypeText = sourcePriceContainer?.querySelector('.price__payment-type')?.textContent?.trim() ?? '';
    const installmentsText = sourcePaymentInfo?.textContent?.trim() ?? '';

    this.refs.comparePrice.textContent = comparePriceText;
    this.refs.comparePrice.hidden = comparePriceText === '';

    this.refs.currentPrice.textContent = currentPriceText;

    this.refs.paymentType.textContent = paymentTypeText;
    this.refs.paymentType.hidden = paymentTypeText === '';

    this.refs.installmentsSummary.textContent = installmentsText;
    this.refs.installmentsSummary.hidden = installmentsText === '';
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }

  #setLoading(isLoading) {
    this.toggleAttribute('data-loading', isLoading);
    this.refs.addToCartButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  #stopLoading() {
    if (this.#loadingIndicatorTimeout) {
      clearTimeout(this.#loadingIndicatorTimeout);
      this.#loadingIndicatorTimeout = undefined;
    }

    this.#setLoading(false);
  }

  #finishSubmit() {
    this.#stopLoading();
    this.#isSubmitting = false;

    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
      this.#targetAddToCartButton.removeAttribute('data-puppet');
    }
  }

  async #playSuccessFeedback() {
    this.#stopLoading();

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) {
      return;
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    await yieldToMainThread();

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
